import React from 'react'
import {
  Alert,
  Box,
  Container,
  FormControl,
  FormLabel,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { MeResponse } from '../../api/me'
import type { PricingPrice, PricingProduct } from '../../api/pricing'
import type { BillingEligibility, BillingSummaryResponse, BillingSubscriptionSummary } from '../../api/billing'
import { createCheckout, createPortal, getBillingSummary, getStationEligibility } from '../../api/billing'
import { usePricingData } from '../../components/pricing/usePricingData'

type PricingTab = 'individual' | 'station'
const currencyFlags: Record<string, string> = {
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  USD: '🇺🇸',
}

export type PricingViewProps = {
  variant: 'public' | 'app'
  me?: MeResponse | null
  isLoggedIn: boolean
}

const PricingView: React.FC<PricingViewProps> = ({ variant, me, isLoggedIn }) => {
  const { currency, setCurrency, supportedCurrencies, pricing, loading, error } = usePricingData('plans')
  const isStationAdmin = React.useMemo(() => {
    if (!me?.stations?.length) {
      return false
    }

    return me.stations.some((station) => station.role === 'admin')
  }, [me?.stations])

  const [tab, setTab] = React.useState<PricingTab>(isStationAdmin ? 'station' : 'individual')
  const [stationId, setStationId] = React.useState<number | null>(null)
  const [seatCounts, setSeatCounts] = React.useState<Record<string, number>>({
    station_inbox: 4,
    station_prep: 4,
    station_bundle: 4,
  })
  const [checkoutLoading, setCheckoutLoading] = React.useState<string | null>(null)
  const [checkoutError, setCheckoutError] = React.useState<string | null>(null)
  const [eligibility, setEligibility] = React.useState<BillingEligibility | null>(null)
  const [eligibilityError, setEligibilityError] = React.useState<string | null>(null)
  const [eligibilityLoading, setEligibilityLoading] = React.useState(false)
  const [portalLoading, setPortalLoading] = React.useState<'user' | 'station' | null>(null)
  const [portalError, setPortalError] = React.useState<string | null>(null)
  const [summary, setSummary] = React.useState<BillingSummaryResponse | null>(null)
  const [summaryLoading, setSummaryLoading] = React.useState(false)
  const [summaryError, setSummaryError] = React.useState<string | null>(null)

  const currencyOptions = React.useMemo(() => {
    const defaults = ['EUR', 'GBP', 'USD']
    return supportedCurrencies.length ? supportedCurrencies : defaults
  }, [supportedCurrencies])

  const renderCurrencyLabel = React.useCallback((value: string) => {
    const code = value.toUpperCase()
    const flag = currencyFlags[code] ?? ''
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {flag ? <span>{flag}</span> : null}
        <span>{code}</span>
      </Box>
    )
  }, [])

  const [autoCheckout, setAutoCheckout] = React.useState<{
    planKey: string
    seats?: number
    stationId?: number
    currency?: string
    tab?: PricingTab
  } | null>(null)

  const products = pricing?.products ?? []
  const productMap = React.useMemo(() => {
    const map = new Map<string, PricingProduct>()
    products.forEach((product) => {
      map.set(product.key, product)
    })
    return map
  }, [products])
  const individualProducts = React.useMemo(() => {
    const items = products.filter((product) => product.group === 'individual' && product.active)
    return items.sort((a, b) => (b.price?.unit_amount ?? -1) - (a.price?.unit_amount ?? -1))
  }, [products])
  const stationProducts = React.useMemo(() => {
    const items = products.filter((product) => product.group === 'station' && product.active)
    return items.sort((a, b) => (b.price?.unit_amount ?? -1) - (a.price?.unit_amount ?? -1))
  }, [products])

  const formatPrice = React.useCallback((amount: number | null, currencyCode: string) => {
    if (amount === null || Number.isNaN(amount)) {
      return 'Contact us'
    }

    const isWhole = amount % 100 === 0

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: isWhole ? 0 : 2,
        maximumFractionDigits: isWhole ? 0 : 2,
      }).format(amount / 100)
    } catch {
      const value = (amount / 100).toFixed(isWhole ? 0 : 2)
      return `${currencyCode} ${value}`
    }
  }, [])

  const intervalLabel = React.useCallback((price?: PricingPrice | null) => {
    if (!price?.interval) {
      return ''
    }

    if (price.interval_count && price.interval_count > 1) {
      return `every ${price.interval_count} ${price.interval}s`
    }

    return `per ${price.interval}`
  }, [])

  const intervalText = React.useCallback((interval?: string | null, count?: number | null) => {
    if (!interval) {
      return ''
    }

    if (count && count > 1) {
      return `per ${count} ${interval}s`
    }

    return `per ${interval}`
  }, [])

  const formatDate = React.useCallback((value?: string | null) => {
    if (!value) {
      return null
    }

    const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`
    const parsed = new Date(normalized)

    if (Number.isNaN(parsed.getTime())) {
      return value
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(parsed)
  }, [])

  const statusLabel = React.useCallback((status: string) => {
    const map: Record<string, string> = {
      active: 'Active',
      trialing: 'Trialing',
      past_due: 'Past due',
      incomplete: 'Incomplete',
      canceled: 'Canceled',
    }

    return map[status] ?? status
  }, [])

  const subscriptionPeriodLabel = React.useCallback(
    (subscription: BillingSubscriptionSummary) => {
      if (subscription.status === 'trialing' && subscription.trial_end) {
        const trialDate = formatDate(subscription.trial_end)
        return trialDate ? `Trial ends ${trialDate}` : null
      }

      if (!subscription.current_period_end) {
        return null
      }

      const periodDate = formatDate(subscription.current_period_end)
      if (!periodDate) {
        return null
      }

      return subscription.cancel_at_period_end ? `Cancels on ${periodDate}` : `Renews on ${periodDate}`
    },
    [formatDate],
  )

  const handleSubscribe = React.useCallback(
    async (planKey: string, seats?: number) => {
      if (!isLoggedIn) {
        const returnUrl = new URL(window.location.href)
        returnUrl.searchParams.set('rpa_plan', planKey)
        returnUrl.searchParams.set('rpa_tab', planKey.startsWith('station_') ? 'station' : 'individual')
        returnUrl.searchParams.set('rpa_currency', currency)
        returnUrl.searchParams.set('rpa_autocheckout', '1')
        if (typeof seats === 'number') {
          returnUrl.searchParams.set('rpa_seats', String(seats))
        }
        if (stationId) {
          returnUrl.searchParams.set('rpa_station_id', String(stationId))
        }
        const loginUrl = new URL('/rpa-login', window.location.origin)
        loginUrl.searchParams.set('redirect_to', returnUrl.toString())
        window.location.assign(loginUrl.toString())
        return
      }

      setCheckoutError(null)
      setCheckoutLoading(planKey)

      try {
        const payload = {
          plan: planKey,
          currency,
          station_id: planKey.startsWith('station_') ? stationId ?? undefined : undefined,
          seat_quantity: planKey.startsWith('station_') && typeof seats === 'number'
            ? Math.max(0, seats - 4)
            : undefined,
        }
        const response = await createCheckout(payload)
        if (response.url) {
          window.location.assign(response.url)
          return
        }
        throw new Error('Stripe checkout did not return a URL.')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to start checkout.'
        setCheckoutError(message)
      } finally {
        setCheckoutLoading(null)
      }
    },
    [currency, isLoggedIn, stationId],
  )

  const handleManageBilling = React.useCallback(
    async (scope: 'user' | 'station', stationOverride?: number) => {
      if (!isLoggedIn) {
        window.location.assign('/rpa-login')
        return
      }

      setPortalError(null)
      setPortalLoading(scope)

      try {
        const targetStationId =
          scope === 'station' ? (stationOverride ?? stationId ?? undefined) : undefined
        const response = await createPortal({
          customer_type: scope,
          station_id: targetStationId,
        })
        if (response.url) {
          window.location.assign(response.url)
          return
        }
        throw new Error('Billing portal did not return a URL.')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to open billing portal.'
        setPortalError(message)
      } finally {
        setPortalLoading(null)
      }
    },
    [isLoggedIn, stationId],
  )

  const checkoutSuccess = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return false
    }
    const params = new URLSearchParams(window.location.search)
    return params.get('checkout') === 'success'
  }, [])

  React.useEffect(() => {
    if (variant !== 'app' || !isLoggedIn) {
      setSummary(null)
      return
    }

    let cancelled = false
    setSummaryLoading(true)
    setSummaryError(null)

    getBillingSummary()
      .then((response) => {
        if (!cancelled) {
          setSummary(response)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unable to load billing summary.'
          setSummaryError(message)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSummaryLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [checkoutSuccess, isLoggedIn, variant])

  React.useEffect(() => {
    if (!autoCheckout) {
      return
    }

    if (autoCheckout.currency && autoCheckout.currency !== currency) {
      setCurrency(autoCheckout.currency)
      return
    }

    if (autoCheckout.tab && autoCheckout.tab !== tab) {
      setTab(autoCheckout.tab)
    }

    if (autoCheckout.stationId && autoCheckout.stationId !== stationId) {
      setStationId(autoCheckout.stationId)
    }

    if (autoCheckout.planKey.startsWith('station_') && autoCheckout.seats) {
      setSeatCounts((prev) => ({ ...prev, [autoCheckout.planKey]: autoCheckout.seats ?? 4 }))
    }

    if (!isLoggedIn || !pricing) {
      return
    }

    if (autoCheckout.planKey.startsWith('station_')) {
      if (!stationId || eligibilityLoading) {
        return
      }
      if (eligibility && !eligibility.can_purchase) {
        return
      }
    }

    void handleSubscribe(autoCheckout.planKey, autoCheckout.seats)
    setAutoCheckout(null)
  }, [
    autoCheckout,
    currency,
    eligibility,
    eligibilityLoading,
    handleSubscribe,
    isLoggedIn,
    pricing,
    setCurrency,
    setSeatCounts,
    stationId,
    tab,
  ])

  React.useEffect(() => {
    if (!me?.stations?.length) {
      setStationId(null)
      return
    }

    setStationId((prev) => (prev && me.stations.some((station) => station.id === prev) ? prev : me.stations[0].id))
  }, [me?.stations])

  React.useEffect(() => {
    if (!isLoggedIn || !stationId) {
      setEligibility(null)
      setEligibilityError(null)
      setEligibilityLoading(false)
      return
    }

    let cancelled = false
    setEligibilityLoading(true)
    setEligibilityError(null)

    getStationEligibility(stationId)
      .then((response) => {
        if (cancelled) {
          return
        }
        setEligibility(response)
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        const message = err instanceof Error ? err.message : 'Unable to check station eligibility.'
        setEligibilityError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setEligibilityLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isLoggedIn, stationId])

  const stationOptions = me?.stations ?? []

  const handleSeatChange = (_planKey: string, value: number | number[] | string) => {
    let parsed = 4
    if (Array.isArray(value)) {
      parsed = value[0] ?? 4
    } else if (typeof value === 'number') {
      parsed = value
    } else {
      const asNumber = Number.parseInt(value, 10)
      parsed = Number.isNaN(asNumber) ? 4 : asNumber
    }

    const next = Math.min(20, Math.max(4, parsed))
    setSeatCounts((prev) => {
      const updated: Record<string, number> = {}
      Object.keys(prev).forEach((key) => {
        updated[key] = next
      })
      return updated
    })
  }

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const params = new URLSearchParams(window.location.search)
    const planKey = params.get('rpa_plan')
    const auto = params.get('rpa_autocheckout') === '1'

    if (!planKey || !auto) {
      return
    }

    const seatsParam = params.get('rpa_seats')
    const stationParam = params.get('rpa_station_id')
    const currencyParam = params.get('rpa_currency')
    const tabParam = params.get('rpa_tab') as PricingTab | null

    const seats = seatsParam ? Number.parseInt(seatsParam, 10) : undefined
    const station = stationParam ? Number.parseInt(stationParam, 10) : undefined
    const normalizedSeats = seats && !Number.isNaN(seats) ? seats : undefined
    const normalizedStation = station && !Number.isNaN(station) ? station : undefined

    setAutoCheckout({
      planKey,
      seats: normalizedSeats,
      stationId: normalizedStation,
      currency: currencyParam ? currencyParam.toUpperCase() : undefined,
      tab: tabParam === 'station' || tabParam === 'individual' ? tabParam : undefined,
    })
  }, [])

  React.useEffect(() => {
    if (isStationAdmin) {
      setTab('station')
      return
    }

    setTab('individual')
  }, [isStationAdmin])

  const renderSubscriptionCard = React.useCallback(
    (subscription: BillingSubscriptionSummary) => {
      const currencyCode = subscription.currency ?? currency
      const totalLabel =
        subscription.total_amount !== null ? formatPrice(subscription.total_amount, currencyCode) : '—'
      const totalInterval = intervalText(subscription.interval, subscription.interval_count)
      const periodLabel = subscriptionPeriodLabel(subscription)
      const primaryLabel =
        subscription.items.find((item) => item.type === 'base')?.label || 'Subscription'

      return (
        <Paper key={subscription.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">{primaryLabel}</Typography>
              <Typography variant="caption" color="text.secondary">
                {statusLabel(subscription.status)}
              </Typography>
            </Stack>
            {subscription.items.length ? (
              <Stack spacing={0.5}>
                {subscription.items.map((item) => {
                  const lineAmount = item.line_total ?? item.unit_amount
                  const lineLabel =
                    lineAmount !== null ? formatPrice(lineAmount, item.currency || currencyCode) : '—'
                  const lineInterval = intervalText(item.interval, item.interval_count)
                  const itemLabel = item.quantity > 1 ? `${item.label} x${item.quantity}` : item.label
                  return (
                    <Stack
                      key={`${subscription.id}-${item.price_id}`}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">{itemLabel}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {lineLabel}
                        {lineInterval ? ` ${lineInterval}` : ''}
                      </Typography>
                    </Stack>
                  )
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No line items found.
              </Typography>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight={600}>
                Total
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {totalLabel}
                {totalInterval ? ` ${totalInterval}` : ''}
              </Typography>
            </Stack>
            {periodLabel ? (
              <Typography variant="caption" color="text.secondary">
                {periodLabel}
              </Typography>
            ) : null}
            {subscription.cancel_at_period_end ? (
              <Typography variant="caption" color="warning.main">
                Cancels at period end.
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      )
    },
    [currency, formatPrice, intervalText, statusLabel, subscriptionPeriodLabel],
  )

  return (
    <Box
      sx={{
        minHeight: variant === 'public' ? '100vh' : 'auto',
        backgroundColor: variant === 'public' ? 'background.default' : 'transparent',
        py: variant === 'public' ? { xs: 6, md: 8 } : 0,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant={variant === 'public' ? 'h3' : 'h4'} component="h1">
              Choose your plan
            </Typography>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, width: { xs: '100%', md: 'auto' } }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <ToggleButtonGroup
                    value={tab}
                    exclusive
                    onChange={(_event, value) => {
                      if (value) {
                        setTab(value)
                      }
                    }}
                    size="small"
                    aria-label="Pricing view"
                  >
                    <ToggleButton value="individual">Individual</ToggleButton>
                    <ToggleButton value="station">Station</ToggleButton>
                  </ToggleButtonGroup>

                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={currency}
                      renderValue={(value) => renderCurrencyLabel(String(value))}
                      onChange={(event) => setCurrency(event.target.value)}
                      disabled={currencyOptions.length <= 1}
                    >
                      {currencyOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {renderCurrencyLabel(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                {error ? <Alert severity="error">{error}</Alert> : null}
                {checkoutError ? <Alert severity="error">{checkoutError}</Alert> : null}
                {portalError ? <Alert severity="error">{portalError}</Alert> : null}
                {checkoutSuccess ? (
                  <Alert severity="success">Checkout completed. Your subscription will update shortly.</Alert>
                ) : null}
              </Stack>
            </Paper>
          </Stack>
          {variant === 'app' && isLoggedIn ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Your subscriptions</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleManageBilling('user')}
                      disabled={portalLoading === 'user'}
                    >
                      Manage billing
                    </Button>
                  </Stack>
                  {summaryLoading ? <LinearProgress /> : null}
                  {summaryError ? <Alert severity="error">{summaryError}</Alert> : null}
                  {summary?.user.subscriptions.length ? (
                    <Stack spacing={1.5}>
                      {summary.user.subscriptions.map((subscription) => renderSubscriptionCard(subscription))}
                    </Stack>
                  ) : !summaryLoading && !summaryError ? (
                    <Typography variant="body2" color="text.secondary">
                      No active subscriptions yet.
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Station subscriptions</Typography>
                  {summary?.stations.length ? (
                    <Stack spacing={2}>
                      {summary.stations.map((station) => (
                        <Paper key={station.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Stack spacing={0.25}>
                                <Typography variant="subtitle1">{station.name || `Station ${station.id}`}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {station.is_admin ? 'Admin access' : 'Presenter access'}
                                </Typography>
                              </Stack>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleManageBilling('station', station.id)}
                                disabled={!station.is_admin || portalLoading === 'station'}
                              >
                                Manage billing
                              </Button>
                            </Stack>
                            {station.subscriptions.length ? (
                              <Stack spacing={1.5}>
                                {station.subscriptions.map((subscription) => renderSubscriptionCard(subscription))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No active subscriptions for this station.
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  ) : !summaryLoading && !summaryError ? (
                    <Typography variant="body2" color="text.secondary">
                      No stations linked to this account.
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>
            </Stack>
          ) : null}
          {loading ? <LinearProgress sx={{ width: '100%' }} /> : null}
          {tab === 'individual' && pricing ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {individualProducts.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography color="text.secondary">No individual plans are available.</Typography>
                </Paper>
              ) : null}
              {individualProducts.map((product) => {
                const price = product.price
                const isAvailable = Boolean(product.available && price)
                const priceLabel = price ? formatPrice(price.unit_amount, price.currency) : 'Not available'
                const bullets = product.bullets ?? []

                return (
                  <Paper key={product.key} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography
                        variant="h6"
                        sx={{ textAlign: 'center', fontWeight: 700, minHeight: '2.4em', lineHeight: 1.2 }}
                      >
                        {product.label || 'Plan'}
                      </Typography>
                      <Typography
                        variant="h4"
                        component="div"
                        sx={{ textAlign: 'center', fontWeight: 700 }}
                      >
                        {priceLabel}{' '}
                        <Typography component="span" color="text.secondary" variant="body2" sx={{ fontWeight: 400 }}>
                          {intervalLabel(price)}
                        </Typography>
                      </Typography>
                      {!isAvailable ? (
                        <Typography color="warning.main">Not available in {currency}</Typography>
                      ) : null}
                      {price?.trial_days ? (
                        <Typography color="text.secondary">{price.trial_days}-day free trial</Typography>
                      ) : null}
                      {product.description ? (
                        <Typography color="text.secondary">{product.description}</Typography>
                      ) : null}
                      {bullets.length ? (
                        <Stack spacing={0.5}>
                          {bullets.map((bullet) => (
                            <Typography key={bullet} variant="body2" color="text.secondary">
                              - {bullet}
                            </Typography>
                          ))}
                        </Stack>
                      ) : null}
                      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSubscribe(product.key)}
                          disabled={checkoutLoading === product.key || !isAvailable}
                        >
                          Continue to checkout
                        </Button>
                        {isLoggedIn ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleManageBilling('user')}
                            disabled={portalLoading === 'user'}
                          >
                            Manage billing
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Paper>
                )
              })}
            </Box>
          ) : null}
          {tab === 'station' && pricing ? (
            <Stack spacing={2}>
              {isLoggedIn ? (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1">Station</Typography>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <FormLabel>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <span>Select station</span>
                          <Tooltip title="Choose which station this subscription should apply to.">
                            <IconButton size="small" aria-label="Station selector info" sx={{ p: 0.25 }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </FormLabel>
                      <Select
                        value={stationId ?? ''}
                        onChange={(event) => setStationId(Number(event.target.value))}
                        displayEmpty
                      >
                        <MenuItem value="">
                          {stationOptions.length ? 'Select station' : 'No stations available'}
                        </MenuItem>
                        {stationOptions.map((station) => (
                          <MenuItem key={station.id} value={station.id}>
                            {station.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {eligibilityLoading ? (
                      <Typography color="text.secondary">Checking station permissions...</Typography>
                    ) : null}
                    {eligibilityError ? <Alert severity="error">{eligibilityError}</Alert> : null}
                    {eligibility && !eligibility.can_purchase ? (
                      <Alert severity="warning">
                        Only station admins can purchase station plans. Ask your station admin for access.
                      </Alert>
                    ) : null}
                    {eligibility && eligibility.can_purchase && !eligibility.has_admins ? (
                      <Alert severity="info">
                        This station has no admins. The purchaser will become the station admin.
                      </Alert>
                    ) : null}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleManageBilling('station')}
                      disabled={!stationId || portalLoading === 'station' || !eligibility?.is_admin}
                    >
                      Manage station billing
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Seat minimum is 4. Need more than 20? Contact us.
                    </Typography>
                  </Stack>
                </Paper>
              ) : null}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                {stationProducts.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Typography color="text.secondary">No station plans are available.</Typography>
                  </Paper>
                ) : null}
                {stationProducts.map((product) => {
                  const planKey = product.key
                  const basePrice = product.price
                  const seatAddonKey = product.seat_addon_key
                  const seatAddon = seatAddonKey ? productMap.get(seatAddonKey) : undefined
                  const seatAddonPrice = seatAddon?.price
                  const bullets = product.bullets ?? []
                  const seats = seatCounts[planKey] ?? 4
                  const extraSeats = Math.max(0, seats - 4)
                  const baseAmount = basePrice?.unit_amount ?? 0
                  const addonAmount = seatAddonPrice?.unit_amount ?? 0
                  const hasAddonPrice = Boolean(seatAddon && seatAddon.available && seatAddonPrice)
                  const isAvailable = Boolean(product.available && basePrice && hasAddonPrice)
                  const total = isAvailable ? baseAmount + addonAmount * extraSeats : null

                  return (
                    <Paper key={planKey} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                      <Stack spacing={1.5}>
                        <Typography
                          variant="h6"
                          sx={{ textAlign: 'center', fontWeight: 700, minHeight: '2.4em', lineHeight: 1.2 }}
                        >
                          {product.label || 'Station plan'}
                        </Typography>
                        <Typography
                          variant="h4"
                          component="div"
                          sx={{ textAlign: 'center', fontWeight: 700 }}
                        >
                          {total !== null
                            ? formatPrice(total, basePrice?.currency || currency)
                            : 'Not available'}{' '}
                          <Typography component="span" color="text.secondary" variant="body2" sx={{ fontWeight: 400 }}>
                            {intervalLabel(basePrice)}
                          </Typography>
                        </Typography>
                        {!isAvailable ? (
                          <Typography color="warning.main">Not available in {currency}</Typography>
                        ) : null}
                        {basePrice?.trial_days ? (
                          <Typography color="text.secondary">{basePrice.trial_days}-day free trial</Typography>
                        ) : null}
                        <Stack spacing={0.75} sx={{ width: '100%' }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">Users</Typography>
                              {hasAddonPrice ? (
                                <Typography variant="caption" color="text.secondary">
                                  +{formatPrice(addonAmount, basePrice?.currency || currency)} / user
                                </Typography>
                              ) : null}
                            </Stack>
                            <Typography variant="body2">{seats}</Typography>
                          </Stack>
                          <Slider
                            value={seats}
                            min={4}
                            max={20}
                            step={1}
                            onChange={(_event, value) => handleSeatChange(planKey, value)}
                            valueLabelDisplay="auto"
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                            Minimum 4 users, max 20.
                          </Typography>
                          {seats >= 20 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                              For more than 20 users{' '}
                              <Link href="/contact" color="inherit" underline="always">
                                contact
                              </Link>{' '}
                              us.
                            </Typography>
                          ) : null}
                        </Stack>
                        {product.description ? (
                          <Typography color="text.secondary">{product.description}</Typography>
                        ) : null}
                        {bullets.length ? (
                          <Stack spacing={0.5}>
                            {bullets.map((bullet) => (
                              <Typography key={bullet} variant="body2" color="text.secondary">
                                - {bullet}
                              </Typography>
                            ))}
                          </Stack>
                        ) : null}
                        <Stack direction="row" justifyContent="center">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSubscribe(planKey, seats)}
                            disabled={
                              checkoutLoading === planKey ||
                              !isAvailable ||
                              (isLoggedIn && (!stationId || (eligibility ? !eligibility.can_purchase : false)))
                            }
                          >
                            Continue to checkout
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  )
                })}
              </Box>
            </Stack>
          ) : null}
          {variant === 'public' && pricing ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: 'center', mt: 1, width: '100%' }}
            >
              Payments and subscriptions are handled by Stripe.com.
            </Typography>
          ) : null}
        </Stack>
      </Container>
    </Box>
  )
}

export default PricingView
